import { ChartBarIcon } from "@heroicons/react/24/outline";
import { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import { useMessages } from "../context/i18n";
import { pricingCards as enPricingCards } from "../dictionaries/en/pricing-cards";
import Typography from "./Typography";
import VerticalStackIcon from "./VerticalStackIcon";

// Avatar-style stack of the social platforms Klap publishes to, shown next
// to the "Unlimited social accounts" benefit. Each logo sits in a white
// circle so the overlapping edges stay readable.
const SocialIconStack: FC<{ isSmall: boolean }> = ({ isSmall }) => {
  const circle = `${
    isSmall ? "w-3.5 h-3.5" : "w-4 h-4"
  } rounded flex items-center justify-center bg-white ring-1 ring-slate-200`;
  const logo = isSmall ? "w-2 h-2" : "w-2.5 h-2.5";
  return (
    <span
      className={`flex items-center shrink-0 ${
        isSmall ? "-space-x-1" : "-space-x-1.5"
      }`}
    >
      <span className={circle}>
        <img src="/svg/TikTok_logomark.svg" alt="TikTok" className={logo} />
      </span>
      <span className={circle}>
        <img
          src="/svg/Instagram_logomark.svg"
          alt="Instagram"
          className={logo}
        />
      </span>
      <span className={circle}>
        <img
          src="/svg/Linkedin_logomark.svg"
          alt="LinkedIn"
          className={logo}
        />
      </span>
      <span className={circle}>
        {/* Shorts_logo.svg has no fill of its own, so inline it to tint it
            YouTube red (same glyph SocialAccountSelector uses). */}
        <svg viewBox="0 0 24 24" className={`${logo} fill-[#FF0000]`}>
          <path d="M17.77 10.3202L16.57 9.82023L18 9.06024C19.84 8.10024 20.53 5.83024 19.56 4.00024C18.59 2.17024 16.32 1.47024 14.49 2.44024L5.99995 6.94024C4.70995 7.62024 3.92995 8.98024 3.99995 10.4302C4.06995 11.8502 4.92995 13.1002 6.21995 13.6802C6.24995 13.6902 7.41995 14.1802 7.41995 14.1802L5.99995 14.9302C4.16995 15.9002 3.46995 18.1702 4.43995 20.0002C5.40995 21.8302 7.67995 22.5302 9.50995 21.5602L18.01 17.0602C19.3 16.3802 20.07 15.0202 20 13.5702C19.93 12.1502 19.06 10.8902 17.77 10.3202ZM9.99995 14.6502V9.35024L15 12.0002L9.99995 14.6502Z" />
        </svg>
      </span>
    </span>
  );
};

// Shared by the `high-conv` pricing page header and the upgrade dialog:
// the three benefits every plan includes, shown as a single row so the
// cards/rows below only have to differentiate on clip quota.
const getBenefits = (
  isSmall: boolean,
  m: typeof enPricingCards,
): { label: string; icon: ReactNode }[] => {
  const iconClass = `${
    isSmall ? "w-3.5 h-3.5" : "w-4 h-4"
  } shrink-0 text-slate-500`;
  return [
    { label: m.aiClipping, icon: <VerticalStackIcon className={iconClass} /> },
    {
      label: m.unlimitedAccounts,
      icon: <SocialIconStack isSmall={isSmall} />,
    },
    { label: m.analytics, icon: <ChartBarIcon className={iconClass} /> },
  ];
};

type PricingBenefitStripProps = {
  /** "m" (default): 16px + 20px icons, the /pricing page header.
   *  "s": 14px + 16px icons, the tighter upgrade dialog. */
  size?: "s" | "m";
} & ComponentPropsWithoutRef<"div">;

const PricingBenefitStrip: FC<PricingBenefitStripProps> = ({
  size = "m",
  ...rest
}) => {
  const isSmall = size === "s";
  const m = useMessages("pricingCards", enPricingCards);
  return (
    <div
      {...rest}
      className={`${rest.className ?? ""} flex flex-col items-center gap-3`}
    >
      <Typography
        variant={isSmall ? "button3" : "subtitle3"}
        className="text-slate-700 !leading-normal"
      >
        {m.allPlansInclude}
      </Typography>
      <ul
        className={`flex flex-wrap items-center justify-center ${
          isSmall ? "gap-2" : "gap-2.5"
        }`}
      >
        {getBenefits(isSmall, m).map(({ label, icon }) => (
          <li
            key={label}
            className={`flex gap-1.5 items-center rounded-full bg-white ring-1 ring-slate-200 ${
              isSmall ? "px-2.5 py-1" : "px-3.5 py-1.5"
            }`}
          >
            {icon}
            <Typography
              variant={isSmall ? "overline" : "caption"}
              className="text-slate-500 !leading-normal"
            >
              {label}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PricingBenefitStrip;
